import {
  memo,
  useCallback,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type PointerEvent as ReactPointerEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { LineChart } from 'lucide-react';
import { cn } from '../../utils/cn';
import { SkeletonLoader } from '../SkeletonLoader';
import { EmptyState } from '../ui/EmptyState';
import { useIsMobile } from '../../hooks/useIsMobile';
import {
  buildAreaPath,
  buildLinePath,
  downsampleRuns,
  inverseScale,
  linearScale,
  nearestIndex,
  niceTicks,
  splitOnGaps,
  timeTicks,
} from '../../utils/chartMath';
import { formatChartTime, timeLabelPattern, type TimeZoneMode } from '../../utils/starchart';
import {
  CHART_GRID_COLOR,
  CHART_LABEL_COLOR,
  CHART_PADDING,
  CHART_POINT_STROKE,
  DEFAULT_MAX_POINTS,
  VIEWBOX_WIDTH,
  type ChartSeries,
} from './chartTypes';

/**
 * Track the rendered width of an element.
 *
 * The other ARGUS charts draw into a fixed 900-unit viewBox, which means the browser scales the
 * drawing uniformly to fit: on a wide card the plot stops short of the right edge, and on a phone
 * it collapses to a third of its stated height with empty space beneath. Measuring instead makes
 * one viewBox unit exactly one CSS pixel, so the chart fills its card at any width and the axis
 * text stays at its intended size rather than being scaled with everything else.
 */
const useElementWidth = (fallback: number) => {
  const [width, setWidth] = useState(fallback);
  const [element, setElement] = useState<HTMLElement | null>(null);

  // A callback ref rather than an object ref: the chart renders a skeleton first, so an effect
  // keyed on a stable ref object would run once against a null target and never see the real
  // element when the data arrives. State makes the element itself the dependency.
  const measureRef = useCallback((node: HTMLElement | null) => setElement(node), []);

  useLayoutEffect(() => {
    if (!element) return;

    const measure = () => {
      const next = element.clientWidth;
      // Ignore a zero measurement from a hidden ancestor; the observer fires again on reveal.
      if (next > 0) setWidth(next);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);

  return { width, measureRef };
};

interface ReferenceLine {
  value: number;
  label?: string;
  color?: string;
}

interface TimeSeriesChartProps {
  series: ChartSeries[];
  height?: number;
  /** Visible time window. Defaults to the union of every series' extent. */
  domain?: [number, number];
  timeZone?: TimeZoneMode;
  valueFormat?: (value: number) => string;
  /** Horizontal markers, e.g. the 20 TPS ceiling. */
  referenceLines?: ReferenceLine[];
  /** Force the y-axis to include zero. Off by default so small variations stay readable. */
  zeroBased?: boolean;
  /**
   * Downsampling strategy. `minmax` cannot drop an extreme, so it is the right choice for tick
   * timings where a single-sample spike is the whole point.
   */
  downsample?: 'lttb' | 'minmax' | 'none';
  maxPoints?: number;
  /** Enables drag-to-zoom. Called once, on release, with the selected range. */
  onBrush?: (startMs: number, endMs: number) => void;
  isLoading?: boolean;
  emptyMessage?: string;
  title?: ReactNode;
  icon?: ComponentType<{ className?: string }>;
  actions?: ReactNode;
  /** Render on a `.subcard` surface, for use inside an existing card. */
  bare?: boolean;
  className?: string;
  ariaLabel?: string;
}

const MOBILE_HEIGHT = 220;
/** Below this drag width a pointer gesture is a click, not a brush. */
const MIN_BRUSH_VIEWBOX_UNITS = 8;
const MIN_BRUSH_MS = 60_000;
/** How near the pointer must be, in viewBox units, for a series to report a value. */
const SNAP_TOLERANCE = 14;

/**
 * Card shell for the chart.
 *
 * Deliberately a module-level component: declaring it inside `TimeSeriesChart` would give it a
 * fresh identity on every render, so React would tear down and rebuild the whole SVG subtree —
 * discarding the width measurement and re-creating hundreds of nodes on every pointer move.
 */
const ChartSurface = ({
  title,
  icon: Icon,
  actions,
  bare,
  className,
  children,
}: {
  title?: ReactNode;
  icon: ComponentType<{ className?: string }>;
  actions?: ReactNode;
  bare?: boolean;
  className?: string;
  children: ReactNode;
}) => (
  <div className={cn(bare ? 'subcard p-4' : 'glass-card p-4 md:p-5', className)}>
    {(title || actions) && (
      <div className="flex items-center justify-between gap-3 mb-3">
        {title && (
          <h3 className="text-sm font-semibold text-white flex items-center gap-2 min-w-0">
            <Icon className="w-4 h-4 text-accent-300 shrink-0" />
            <span className="truncate">{title}</span>
          </h3>
        )}
        {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
      </div>
    )}
    {children}
  </div>
);

const ChartSkeleton = ({ height, bare }: { height: number; bare?: boolean }) => (
  <div className={cn(bare ? 'subcard p-4' : 'glass-card p-4 md:p-5')} aria-busy="true">
    <div className="flex items-center justify-between mb-4">
      <SkeletonLoader className="h-4 w-40" />
      <SkeletonLoader className="h-3 w-16" />
    </div>
    <div className="skeleton w-full rounded-lg" style={{ height }} aria-hidden="true" />
  </div>
);

/**
 * Multi-series time chart.
 *
 * Hand-written SVG in the house idiom (fixed 900-unit viewBox, `PADDING` constant, colours from
 * CSS custom properties) rather than a charting dependency. Beyond what the existing ARGUS charts
 * do it adds several series on shared axes, gap-aware paths, downsampling that survives 30 000
 * samples, drag-to-zoom, and keyboard traversal.
 *
 * The static plot — grid, axes, ticks, paths — is memoised into an element tree so that moving
 * the pointer only re-renders the crosshair group, not several hundred nodes.
 */
export const TimeSeriesChart = memo(
  ({
    series,
    height,
    domain,
    timeZone = 'local',
    valueFormat = (v) => v.toLocaleString(undefined, { maximumFractionDigits: 2 }),
    referenceLines,
    zeroBased = false,
    downsample = 'lttb',
    maxPoints = DEFAULT_MAX_POINTS,
    onBrush,
    isLoading,
    emptyMessage = 'No data for this range.',
    title,
    icon: Icon = LineChart,
    actions,
    bare = false,
    className,
    ariaLabel,
  }: TimeSeriesChartProps) => {
    const isMobile = useIsMobile();
    const svgRef = useRef<SVGSVGElement>(null);
    const gradientId = useId();
    const frameRef = useRef<number | null>(null);
    const { width: viewBoxWidth, measureRef } = useElementWidth(VIEWBOX_WIDTH);

    const [hoverX, setHoverX] = useState<number | null>(null);
    const [brush, setBrush] = useState<{ from: number; to: number } | null>(null);
    /** Series the viewer has switched off from the legend, on top of any `hidden` prop. */
    const [mutedIds, setMutedIds] = useState<Set<string>>(new Set());

    const chartHeight = height ?? (isMobile ? MOBILE_HEIGHT : 300);
    const plotWidth = Math.max(80, viewBoxWidth - CHART_PADDING.left - CHART_PADDING.right);
    const plotHeight = chartHeight - CHART_PADDING.top - CHART_PADDING.bottom;
    const baselineY = CHART_PADDING.top + plotHeight;

    const visible = useMemo(
      () => series.filter((s) => !s.hidden && !mutedIds.has(s.id) && s.points.length > 0),
      [series, mutedIds],
    );

    /** Every series with data, muted or not — the legend has to keep offering the muted ones. */
    const legendSeries = useMemo(
      () => series.filter((s) => !s.hidden && s.points.length > 0),
      [series],
    );

    const toggleSeries = useCallback((id: string) => {
      setMutedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    }, []);

    /** Last recorded value per series, so the legend carries a number and not just a name. */
    const latestValues = useMemo(() => {
      const map = new Map<string, number>();
      for (const entry of legendSeries) {
        for (let i = entry.points.length - 1; i >= 0; i -= 1) {
          const value = entry.points[i].v;
          if (value !== null && isFinite(value)) {
            map.set(entry.id, value);
            break;
          }
        }
      }
      return map;
    }, [legendSeries]);

    const geometry = useMemo(() => {
      if (visible.length === 0) return null;

      let xMin = Infinity;
      let xMax = -Infinity;
      let yMin = Infinity;
      let yMax = -Infinity;

      for (const entry of visible) {
        for (const point of entry.points) {
          if (point.t < xMin) xMin = point.t;
          if (point.t > xMax) xMax = point.t;
          if (point.v === null || !isFinite(point.v)) continue;
          if (point.v < yMin) yMin = point.v;
          if (point.v > yMax) yMax = point.v;
        }
      }

      if (domain) {
        [xMin, xMax] = domain;
      }
      if (!isFinite(xMin) || !isFinite(xMax) || xMax <= xMin) return null;
      // Every sample was null: there is a time axis but nothing to plot against it.
      if (!isFinite(yMin) || !isFinite(yMax)) {
        yMin = 0;
        yMax = 1;
      }

      for (const line of referenceLines ?? []) {
        if (line.value < yMin) yMin = line.value;
        if (line.value > yMax) yMax = line.value;
      }
      if (zeroBased) yMin = Math.min(0, yMin);

      const { ticks: yTicks, niceMin, niceMax } = niceTicks(yMin, yMax, 5);
      const xTicks = timeTicks(xMin, xMax, isMobile ? 4 : 7);
      const pattern = timeLabelPattern(xMax - xMin);

      const toX = (t: number) =>
        linearScale(t, xMin, xMax, CHART_PADDING.left, CHART_PADDING.left + plotWidth);
      const toY = (v: number) => linearScale(v, niceMin, niceMax, baselineY, CHART_PADDING.top);

      const plotted = visible.map((entry) => {
        const runs = downsampleRuns(splitOnGaps(entry.points), maxPoints, downsample);
        const projected = runs.map((run) => run.map((p) => ({ x: toX(p.t), y: toY(p.v) })));
        return {
          entry,
          // Flattened, still in time order — the hover lookup binary-searches this.
          samples: runs.flat(),
          linePath: buildLinePath(projected),
          areaPath:
            entry.kind === 'area' && visible.length === 1
              ? buildAreaPath(projected, baselineY)
              : '',
        };
      });

      return { xMin, xMax, niceMin, niceMax, yTicks, xTicks, pattern, toX, toY, plotted };
    }, [
      visible,
      domain,
      referenceLines,
      zeroBased,
      isMobile,
      plotWidth,
      baselineY,
      maxPoints,
      downsample,
    ]);

    /**
     * The static half of the chart.
     *
     * Kept as its own memo so a pointer move reconciles only the crosshair group below — with
     * several hundred tick and path nodes, re-rendering this on every mouse event is the
     * difference between a smooth chart and a janky one.
     */
    const plot = useMemo(() => {
      if (!geometry) return null;
      const { yTicks, xTicks, toX, toY, pattern, plotted, niceMin, niceMax } = geometry;

      return (
        <g>
          {yTicks.map((tick) => {
            const y = toY(tick);
            if (y < CHART_PADDING.top - 1 || y > baselineY + 1) return null;
            return (
              <g key={`y-${tick}`}>
                <line
                  x1={CHART_PADDING.left}
                  y1={y}
                  x2={CHART_PADDING.left + plotWidth}
                  y2={y}
                  stroke={CHART_GRID_COLOR}
                  strokeDasharray="4,4"
                  vectorEffect="non-scaling-stroke"
                />
                <text
                  x={CHART_PADDING.left - 8}
                  y={y + 4}
                  fill={CHART_LABEL_COLOR}
                  fontSize={11}
                  textAnchor="end"
                >
                  {valueFormat(tick)}
                </text>
              </g>
            );
          })}

          {(referenceLines ?? []).map((line) => {
            const y = toY(line.value);
            if (y < CHART_PADDING.top || y > baselineY) return null;
            return (
              <g key={`ref-${line.value}`}>
                <line
                  x1={CHART_PADDING.left}
                  y1={y}
                  x2={CHART_PADDING.left + plotWidth}
                  y2={y}
                  stroke={line.color ?? CHART_LABEL_COLOR}
                  strokeDasharray="2,6"
                  strokeOpacity={0.7}
                  vectorEffect="non-scaling-stroke"
                />
                {line.label && (
                  <text
                    x={CHART_PADDING.left + plotWidth}
                    y={y - 4}
                    fill={line.color ?? CHART_LABEL_COLOR}
                    fontSize={10}
                    textAnchor="end"
                  >
                    {line.label}
                  </text>
                )}
              </g>
            );
          })}

          {xTicks.map((tick) => (
            <text
              key={`x-${tick}`}
              x={toX(tick)}
              y={chartHeight - 8}
              fill={CHART_LABEL_COLOR}
              fontSize={11}
              textAnchor="middle"
            >
              {formatChartTime(tick, pattern, timeZone)}
            </text>
          ))}

          {plotted.map(({ entry, areaPath, linePath }) => (
            <g key={entry.id}>
              {areaPath && (
                <path d={areaPath} fill={`url(#${gradientId})`} stroke="none" />
              )}
              <path
                d={linePath}
                fill="none"
                stroke={entry.color}
                strokeWidth={2}
                strokeLinejoin="round"
                strokeLinecap="round"
                strokeDasharray={entry.dashed ? '6,4' : undefined}
                vectorEffect="non-scaling-stroke"
              />
            </g>
          ))}

          {/* Referenced by the area fill; the stop colour follows the single series. */}
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={plotted[0]?.entry.color} stopOpacity="0.28" />
              <stop offset="100%" stopColor={plotted[0]?.entry.color} stopOpacity="0" />
            </linearGradient>
          </defs>

          <title>
            {ariaLabel ??
              `${visible.map((s) => s.label).join(', ')} between ${formatChartTime(
                geometry.xMin,
                'PPpp',
                timeZone,
              )} and ${formatChartTime(geometry.xMax, 'PPpp', timeZone)}, ranging from ${valueFormat(
                niceMin,
              )} to ${valueFormat(niceMax)}`}
          </title>
        </g>
      );
    }, [
      geometry,
      plotWidth,
      baselineY,
      chartHeight,
      isMobile,
      valueFormat,
      referenceLines,
      timeZone,
      gradientId,
      ariaLabel,
      visible,
    ]);

    /** Values under the crosshair, one per visible series. */
    const readout = useMemo(() => {
      if (!geometry || hoverX === null) return null;
      const t = inverseScale(
        hoverX,
        geometry.xMin,
        geometry.xMax,
        CHART_PADDING.left,
        CHART_PADDING.left + plotWidth,
      );

      const entries = geometry.plotted.map(({ entry, samples }) => {
        const index = nearestIndex(samples, t);
        const sample = index >= 0 ? samples[index] : null;
        if (!sample) return { entry, sample: null as null | { t: number; v: number } };
        // Outside the snap tolerance the series genuinely has nothing here — usually a gap.
        const distance = Math.abs(geometry.toX(sample.t) - hoverX);
        return { entry, sample: distance <= SNAP_TOLERANCE ? sample : null };
      });

      return { t, entries };
    }, [geometry, hoverX, plotWidth]);

    const viewBoxX = useCallback((clientX: number): number | null => {
      const svg = svgRef.current;
      if (!svg) return null;
      const rect = svg.getBoundingClientRect();
      if (rect.width === 0) return null;
      // One viewBox unit is one CSS pixel now, but keep the ratio so a mid-resize pointer move
      // still maps correctly.
      return ((clientX - rect.left) / rect.width) * viewBoxWidth;
    }, [viewBoxWidth]);

    // Pointer moves arrive far faster than frames; coalescing to one update per frame keeps a
    // fast drag from queueing dozens of renders.
    const scheduleHover = useCallback((x: number | null) => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = null;
        setHoverX(x);
      });
    }, []);

    const handlePointerMove = useCallback(
      (event: ReactPointerEvent<SVGSVGElement>) => {
        const x = viewBoxX(event.clientX);
        if (x === null) return;
        scheduleHover(x);
        setBrush((prev) => (prev ? { ...prev, to: x } : prev));
      },
      [viewBoxX, scheduleHover],
    );

    const handlePointerDown = useCallback(
      (event: ReactPointerEvent<SVGSVGElement>) => {
        if (!onBrush || event.button !== 0) return;
        const x = viewBoxX(event.clientX);
        if (x === null) return;
        // Capture so a drag that leaves the SVG still finishes here rather than being lost.
        event.currentTarget.setPointerCapture(event.pointerId);
        setBrush({ from: x, to: x });
      },
      [onBrush, viewBoxX],
    );

    const handlePointerUp = useCallback(
      (event: ReactPointerEvent<SVGSVGElement>) => {
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
          event.currentTarget.releasePointerCapture(event.pointerId);
        }
        const current = brush;
        setBrush(null);
        if (!current || !onBrush || !geometry) return;
        if (Math.abs(current.to - current.from) < MIN_BRUSH_VIEWBOX_UNITS) return;

        const toTime = (x: number) =>
          inverseScale(
            x,
            geometry.xMin,
            geometry.xMax,
            CHART_PADDING.left,
            CHART_PADDING.left + plotWidth,
          );

        const a = toTime(Math.min(current.from, current.to));
        const b = toTime(Math.max(current.from, current.to));
        if (b - a >= MIN_BRUSH_MS) onBrush(a, b);
      },
      [brush, onBrush, geometry, plotWidth],
    );

    const handleKeyDown = useCallback(
      (event: ReactKeyboardEvent<SVGSVGElement>) => {
        if (!geometry) return;
        const step = (event.shiftKey ? 10 : 1) * (plotWidth / 60);

        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          const direction = event.key === 'ArrowLeft' ? -1 : 1;
          setHoverX((prev) => {
            const base = prev ?? CHART_PADDING.left + plotWidth / 2;
            return Math.min(
              CHART_PADDING.left + plotWidth,
              Math.max(CHART_PADDING.left, base + direction * step),
            );
          });
        } else if (event.key === 'Home') {
          event.preventDefault();
          setHoverX(CHART_PADDING.left);
        } else if (event.key === 'End') {
          event.preventDefault();
          setHoverX(CHART_PADDING.left + plotWidth);
        } else if (event.key === 'Escape') {
          setBrush(null);
          setHoverX(null);
        }
      },
      [geometry, plotWidth],
    );

    if (isLoading) return <ChartSkeleton height={chartHeight} bare={bare} />;

    const surfaceProps = { title, icon: Icon, actions, bare, className };

    if (!geometry) {
      return (
        <ChartSurface {...surfaceProps}>
          {/* Still rendered inside the measuring div so a later data arrival has a width. */}
          <div ref={measureRef} className="w-full">
            <EmptyState
              bare
              compact
              icon={LineChart}
              title="Nothing to plot"
              description={emptyMessage}
            />
          </div>
        </ChartSurface>
      );
    }

    const brushRect = brush
      ? { x: Math.min(brush.from, brush.to), width: Math.abs(brush.to - brush.from) }
      : null;

    return (
      <ChartSurface {...surfaceProps}>
        <div ref={measureRef} className="w-full">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${viewBoxWidth} ${chartHeight}`}
          className="w-full select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-400 rounded-lg"
          style={{
            height: chartHeight,
            // Vertical page scroll still belongs to the page; horizontal drag is the brush.
            touchAction: onBrush ? 'pan-y' : 'auto',
            cursor: onBrush ? 'crosshair' : 'default',
          }}
          role="img"
          tabIndex={0}
          aria-label={ariaLabel ?? `Time series: ${visible.map((s) => s.label).join(', ')}`}
          onPointerMove={handlePointerMove}
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onPointerLeave={() => scheduleHover(null)}
          onKeyDown={handleKeyDown}
        >
          {plot}

          {brushRect && brushRect.width > 0 && (
            <rect
              x={brushRect.x}
              y={CHART_PADDING.top}
              width={brushRect.width}
              height={plotHeight}
              fill="rgba(var(--ring-rgb), 0.14)"
              stroke="var(--accent-300)"
              strokeOpacity={0.5}
              vectorEffect="non-scaling-stroke"
            />
          )}

          {hoverX !== null && readout && (
            <g pointerEvents="none">
              <line
                x1={hoverX}
                y1={CHART_PADDING.top}
                x2={hoverX}
                y2={baselineY}
                stroke={CHART_LABEL_COLOR}
                strokeOpacity={0.5}
                strokeDasharray="3,3"
                vectorEffect="non-scaling-stroke"
              />
              {readout.entries.map(({ entry, sample }) =>
                sample ? (
                  <circle
                    key={entry.id}
                    cx={geometry.toX(sample.t)}
                    cy={geometry.toY(sample.v)}
                    r={4}
                    fill={entry.color}
                    stroke={CHART_POINT_STROKE}
                    strokeWidth={1.5}
                  />
                ) : null,
              )}
            </g>
          )}
        </svg>
        </div>

        {/* The readout is HTML, not SVG text, so it can use the type scale and wrap properly. */}
        <div
          className="mt-2 min-h-[1.25rem] flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs tabular-nums"
          aria-live="polite"
        >
          {readout ? (
            <>
              <span className="text-glass-400">
                {formatChartTime(readout.t, 'PPp', timeZone)}
                {timeZone === 'utc' ? ' UTC' : ''}
              </span>
              {readout.entries.map(({ entry, sample }) => (
                <span key={entry.id} className="flex items-center gap-1.5">
                  <span
                    className="w-3 h-0.5 rounded shrink-0"
                    style={{ background: entry.color }}
                    aria-hidden="true"
                  />
                  <span className="text-glass-400">{entry.label}</span>
                  <span className="font-medium text-white">
                    {sample ? (entry.valueFormat ?? valueFormat)(sample.v) : '—'}
                  </span>
                </span>
              ))}
            </>
          ) : (
            <span className="text-glass-500">
              {onBrush ? 'Drag to zoom into a range. Arrow keys move the cursor.' : ''}
            </span>
          )}
        </div>

        {/*
          A persistent legend, not just the hover readout: with several lines on one chart you
          cannot tell them apart without one, and switching a series off is the quickest way to
          read a crowded comparison.
        */}
        {legendSeries.length > 1 && (
          <div className="mt-2 pt-2 border-t border-glass-200/10 flex flex-wrap items-center gap-x-3 gap-y-1.5">
            {legendSeries.map((entry) => {
              const muted = mutedIds.has(entry.id);
              const latest = latestValues.get(entry.id);
              return (
                <button
                  key={entry.id}
                  type="button"
                  onClick={() => toggleSeries(entry.id)}
                  aria-pressed={!muted}
                  title={muted ? `Show ${entry.label}` : `Hide ${entry.label}`}
                  className={cn(
                    'inline-flex items-center gap-1.5 text-xs rounded-md px-1.5 py-1 transition-colors',
                    'hover:bg-glass-200/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-400',
                    muted && 'opacity-40',
                  )}
                >
                  <span
                    className="w-3 h-0.5 rounded shrink-0"
                    style={{
                      background: entry.color,
                      // A dashed swatch for a dashed line, so the legend matches the chart.
                      backgroundImage: entry.dashed
                        ? `repeating-linear-gradient(90deg, ${entry.color} 0 3px, transparent 3px 6px)`
                        : undefined,
                    }}
                    aria-hidden="true"
                  />
                  <span className={cn('truncate max-w-[14rem]', muted ? 'text-glass-500' : 'text-glass-300')}>
                    {entry.label}
                  </span>
                  {latest !== undefined && (
                    <span className={cn('tabular-nums', muted ? 'text-glass-500' : 'text-white font-medium')}>
                      {(entry.valueFormat ?? valueFormat)(latest)}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Screen readers get a summary instead of several hundred unreadable data points. */}
        <table className="sr-only">
          <caption>Series summary</caption>
          <thead>
            <tr>
              <th scope="col">Series</th>
              <th scope="col">First</th>
              <th scope="col">Last</th>
              <th scope="col">Minimum</th>
              <th scope="col">Maximum</th>
            </tr>
          </thead>
          <tbody>
            {geometry.plotted.map(({ entry, samples }) => {
              if (samples.length === 0) return null;
              const values = samples.map((s) => s.v);
              return (
                <tr key={entry.id}>
                  <th scope="row">{entry.label}</th>
                  <td>{valueFormat(values[0])}</td>
                  <td>{valueFormat(values[values.length - 1])}</td>
                  <td>{valueFormat(Math.min(...values))}</td>
                  <td>{valueFormat(Math.max(...values))}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </ChartSurface>
    );
  },
);

TimeSeriesChart.displayName = 'TimeSeriesChart';
